import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import AuthImage from "../../components/AuthImage";

// Aplana el orden personal (destacados + categorías > subcategorías >
// productos) en una sola lista de ids, igual que _flatten_orden en el
// backend (orden_compra.py) — se usa aquí para poder aplicar, sin ir al
// servidor, un borrador sin guardar que el comprador tenga en localStorage
// desde el panel "Ordenar compra" (ese borrador debe prevalecer sobre el
// orden ya guardado en la BBDD mientras no se guarde).
function flattenOrdenLocal(orden) {
  const flat = [];
  const vistos = new Set();
  const add = (id) => { if (!vistos.has(id)) { vistos.add(id); flat.push(id); } };
  (orden?.destacados || []).forEach(add);
  const subcatsPorCat = orden?.subcategorias || {};
  const productosPorSub = orden?.productos || {};
  (orden?.categorias || []).forEach((idCat) => {
    (subcatsPorCat[idCat] || []).forEach((idSub) => {
      (productosPorSub[idSub] || []).forEach(add);
    });
  });
  return flat;
}

const DetalleCompra = () => {
  const { user } = useAuth();
  const usuario = user?.user || user;

  const location = useLocation();
  const navigate = useNavigate();

  // 🔹 Lee el state que venga por ruta (puede estar vacío tras back+cancel)
  const routeState = location.state || {};

  // 🔹 Intenta reconstruir el último id y su state persistido
  const lastId = sessionStorage.getItem("detalleCompra:lastId");
  const savedStateRaw =
    lastId && sessionStorage.getItem(`detalleCompra:${lastId}:state`);
  // Memorizado por el texto guardado: si se parseara en cada render, `productos`
  // sería una lista nueva cada vez y el efecto de carga de imágenes (que depende
  // de ella) se relanzaría en bucle -> miles de POST /productos/imagenes por
  // minuto cuando se entra sin state de ruta (recarga, volver de segundo plano).
  const savedState = useMemo(() => {
    try {
      return savedStateRaw ? JSON.parse(savedStateRaw) : null;
    } catch {
      return null;
    }
  }, [savedStateRaw]);

  // 🔹 Deriva los datos con prioridad: routeState -> sessionStorage -> defaults
  const id =
    routeState.id ?? savedState?.id ?? null;
  const abreviatura =
    routeState.abreviatura ?? savedState?.abreviatura ?? "";
  const id_piso = routeState.id_piso ?? savedState?.id_piso ?? null;
  const precio_total = Number(
    routeState.precio_total ?? savedState?.precio_total ?? 0
  );
  const productos =
    routeState.productos ?? savedState?.productos ?? null;

  // Notas de la supervisora: clave y flag de presencia
  const NOTES_KEY = "notes:supervisora";
  const hasNotasSupervisora =
    !!(routeState.notas_supervisora && String(routeState.notas_supervisora).trim() !== "");

  // 🔹 Persistir SIEMPRE el último state válido que llegue por ruta
  useEffect(() => {
    if (routeState?.id && routeState?.productos) {
      sessionStorage.setItem("detalleCompra:lastId", String(routeState.id));
      sessionStorage.setItem(
        `detalleCompra:${routeState.id}:state`,
        JSON.stringify({
          id: routeState.id,
          id_piso: routeState.id_piso ?? null,
          abreviatura: routeState.abreviatura ?? "",
          precio_total: Number(routeState.precio_total ?? 0),
          productos: routeState.productos,
        })
      );
      sessionStorage.setItem(
        `detalleCompra:${routeState.id}:productos`,
        JSON.stringify(routeState.productos)
      );
    }
  }, [routeState]);

  const isConfirmingRef = useRef(false);
  const isDraggingRef = useRef(false);
  const containerRef = useRef(null);
  const dragEndTimeoutRef = useRef(null);
  const droppableRef = useRef(null);

  const storageKey = id ? `detalleCompra:${id}:selectedIds` : null;
  const hasHydrated = useRef(false);

  const [precioInput, setPrecioInput] = useState(Number(precio_total || 0).toFixed(2));
  const [notas, setNotas] = useState("");
  const [ticketFile, setTicketFile] = useState(null);

  const handlePrecioChange = (e) => setPrecioInput(e.target.value);
  const handleNotasChange = (e) => setNotas(e.target.value);
  const handleFileChange = (e) => setTicketFile(e.target.files[0] || null);

  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    precio: precio_total || 0,
    notas: "",
    ticketFile: null,
  });
  
  // Toast notifications
  const [toast, setToast] = useState({ visible: false, message: "", type: "info" });
  const toastTimerRef = useRef(null);
  const showToast = (message, type = "info", timeout = 2500) => {
    setToast({ visible: true, message, type });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast({ visible: false, message: "", type }), timeout);
  };

  // Confirmaciones vistosas reutilizables
  const [confirmData, setConfirmData] = useState({ open: false, title: "", message: "", onConfirm: null, variant: "info" });
  const openConfirm = ({ title = "Confirmar", message, onConfirm, variant = "info" }) => setConfirmData({ open: true, title, message, onConfirm, variant });
  const closeConfirm = () => setConfirmData({ open: false, title: "", message: "", onConfirm: null, variant: "info" });
  
  // Selector de supermercado
  const [supermercadoPiso, setSupermercadoPiso] = useState(null);
  const [empresaPiso, setEmpresaPiso] = useState(null);
  const [supermercadoElegido, setSupermercadoElegido] = useState("");
  const [pisoInfoCargado, setPisoInfoCargado] = useState(false);
  const [mostrarPromptSupermercado, setMostrarPromptSupermercado] = useState(false);

  // Cambios de la compra para ocultar eliminados
  const [eliminadosSet, setEliminadosSet] = useState(() => new Set());

  // Cargar cambios al montar
  useEffect(() => {
    const fetchCambios = async () => {
      try {
        if (!id) return;
        const r = await axios.get(`/api/compras/cambios`, { params: { id_compra: id } });
        const cambios = Array.isArray(r.data?.cambios) ? r.data.cambios : [];
        const eliminados = new Set(cambios.filter(c => c.id_producto_añadido === 1).map(c => c.id_producto_eliminado));
        setEliminadosSet(eliminados);
      } catch (e) {
        // no bloquear vista
      }
    };
    fetchCambios();
  }, [id]);

  // Cargar supermercado del piso
  useEffect(() => {
    const fetchPiso = async () => {
      try {
        if (!id_piso) { setSupermercadoPiso(null); return; }
        const r = await axios.get(`/api/pisos/${id_piso}`);
        const sup = (r.data?.supermercado ?? null);
        setSupermercadoPiso(sup);
        setEmpresaPiso(r.data?.empresa ?? null);
      } catch (e) {
        setSupermercadoPiso(null);
      } finally {
        setPisoInfoCargado(true);
      }
    };
    fetchPiso();
  }, [id_piso]);

  // Los pisos de empresa CH-SS nunca piden supermercado (no compran en uno
  // real). El resto, si el piso no tiene uno fijo asignado, tiene que
  // elegirlo — pero ahora se pregunta UNA VEZ, al entrar (antes se preguntaba
  // otra vez al confirmar la compra, con el modal bloqueante de abajo), y se
  // guarda en sessionStorage (front) hasta que la compra se confirma de
  // verdad — el backend no se entera de la elección hasta ese momento.
  const pisoNecesitaEleccion = (!supermercadoPiso || (typeof supermercadoPiso === 'string' && supermercadoPiso.trim() === '-')) && empresaPiso !== 'CH-SS';
  const supermercadoKey = id ? `compra_${id}_supermercado_elegido` : null;

  useEffect(() => {
    if (!pisoInfoCargado || !id) return;
    if (!pisoNecesitaEleccion) { setMostrarPromptSupermercado(false); return; }
    if (supermercadoElegido === 'B' || supermercadoElegido === 'M') { setMostrarPromptSupermercado(false); return; }
    try {
      const guardado = sessionStorage.getItem(supermercadoKey);
      if (guardado === 'B' || guardado === 'M') {
        setSupermercadoElegido(guardado);
        setMostrarPromptSupermercado(false);
        return;
      }
    } catch (e) { /* ignore */ }
    setMostrarPromptSupermercado(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pisoInfoCargado, pisoNecesitaEleccion, id]);

  const elegirSupermercadoInicial = (s) => {
    setSupermercadoElegido(s);
    setMostrarPromptSupermercado(false);
    try { if (supermercadoKey) sessionStorage.setItem(supermercadoKey, s); } catch (e) { /* ignore */ }
  };

  // Supermercado que se usa para pedir el orden personal de productos:
  // el fijo del piso si lo tiene, o el que acaba de elegir el comprador.
  const supermercadoNormalizado = (supermercadoPiso || "").trim().toUpperCase();
  const supermercadoEfectivo = pisoNecesitaEleccion
    ? (supermercadoElegido === 'B' || supermercadoElegido === 'M' ? supermercadoElegido : null)
    : (supermercadoNormalizado === 'B' || supermercadoNormalizado === 'M' ? supermercadoNormalizado : null);

  // Última combinación de productos/supermercado para la que ya se cargaron
  // las imágenes: segunda protección contra recargas repetidas del efecto.
  const ultimaCargaImagenesRef = useRef("");

  // ========= Carga de items (con imágenes y orden guardado) =========
  useEffect(() => {
    if (!productos || !id) return;
    // Espera a saber si este piso necesita elegir supermercado y, si lo
    // necesita, a que el comprador ya haya respondido al modal de abajo —
    // así la lista de productos aparece directamente con el orden correcto,
    // sin mostrarla primero "sin ordenar" y reordenarla después.
    if (!pisoInfoCargado) return;
    if (pisoNecesitaEleccion && !supermercadoEfectivo) return;

    const makeKey = (p, idx) =>
      p.__key ||
      (p?.id != null
        ? `id:${p.id}`
        : `tmp:${String(p?.nombre ?? "")}|${String(p?.cantidad ?? "")}|${String(p?.precio ?? "")}|${idx}`);

    const cargarImagenes = async () => {
      const productosVisibles = (productos || []).filter((p) => !eliminadosSet.has(p.id));
      const ids = productosVisibles.filter((p) => typeof p.id === "number").map((p) => p.id);
      const claveCarga = `${id}|${supermercadoEfectivo || ""}|${JSON.stringify(productosVisibles)}`;
      if (claveCarga === ultimaCargaImagenesRef.current) return;
      ultimaCargaImagenesRef.current = claveCarga;
      try {
        const res = await axios.post(`/api/productos/imagenes`, { ids });
        const imagenes = res.data;
        const productosConImagen = productosVisibles.map((p) => ({
          ...p,
          imagen: imagenes[p.id] ?? p.imagen ?? null,
        }));

        const key = `compra_${id}_orden`;
        const ordenGuardado = localStorage.getItem(key);

        let itemsOrdenados;
        if (ordenGuardado) {
          try {
            const ordenIds = JSON.parse(ordenGuardado);
            itemsOrdenados = ordenIds
              .map((oid) => productosConImagen.find((p) => p.id === oid))
              .filter(Boolean);
            const faltantes = productosConImagen.filter(
              (p) => !ordenIds.includes(p.id)
            );
            itemsOrdenados = [...itemsOrdenados, ...faltantes];
          } catch {
            itemsOrdenados = [...productosConImagen];
          }
        } else {
          // Sin orden manual guardado para ESTA compra: se usa como orden por
          // defecto el orden personal del comprador para este supermercado
          // (panel "Ordenar compra"). Si tiene un borrador sin guardar en
          // localStorage (cambios hechos en ese panel pero sin pulsar
          // "Guardar"), ese borrador prevalece sobre lo que haya en la BBDD.
          // Si no hay nada de nada, se deja el orden tal cual llega.
          let ordenIdsGlobal = [];
          if (supermercadoEfectivo) {
            let ordenBorrador = null;
            try {
              const raw = localStorage.getItem(`orden_compra_draft_${usuario}_${supermercadoEfectivo}`);
              if (raw) ordenBorrador = JSON.parse(raw);
            } catch (e) { /* ignore */ }
            if (ordenBorrador) {
              ordenIdsGlobal = flattenOrdenLocal(ordenBorrador);
            } else {
              try {
                const resOrden = await axios.get(`/api/orden-compra/mio?supermercado=${supermercadoEfectivo}`);
                ordenIdsGlobal = resOrden.data?.orden_ids || [];
              } catch {
                ordenIdsGlobal = [];
              }
            }
          }
          if (ordenIdsGlobal.length) {
            const primero = ordenIdsGlobal
              .map((oid) => productosConImagen.find((p) => p.id === oid))
              .filter(Boolean);
            const resto = productosConImagen.filter((p) => !ordenIdsGlobal.includes(p.id));
            itemsOrdenados = [...primero, ...resto];
          } else {
            itemsOrdenados = [...productosConImagen];
          }
        }

        const withKeys = itemsOrdenados.map((p, idx) => ({
          ...p,
          __key: makeKey(p, idx),
        }));
        setItems(withKeys);
      } catch (error) {
        console.error("Error al cargar imágenes:", error);
        const withKeys = productosVisibles.map((p, idx) => ({
          ...p,
          __key: makeKey(p, idx),
        }));
        setItems(withKeys);
      }
    };

    cargarImagenes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, productos, eliminadosSet, pisoInfoCargado, pisoNecesitaEleccion, supermercadoEfectivo]);

  // ========= 🔹 MEJORA 1: Persistir selectedIds en localStorage Y sessionStorage =========
  useEffect(() => {
    if (!items.length || hasHydrated.current || !storageKey) return;

    // Intenta primero sessionStorage, luego localStorage como fallback
    let raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      raw = localStorage.getItem(storageKey);
    }

    if (raw) {
      try {
        const saved = JSON.parse(raw);
        const valid = new Set([
          ...items.map((it) => it.__key),
          ...(hasNotasSupervisora ? [NOTES_KEY] : []),
        ]);
        const filtered = saved.filter((k) => valid.has(k));
        setSelectedIds(new Set(filtered));
      } catch {
        // ignore
      }
    }
    hasHydrated.current = true;
  }, [items, storageKey, hasNotasSupervisora]);

  useEffect(() => {
    if (!hasHydrated.current || !storageKey) return;
    const data = JSON.stringify([...selectedIds]);
    // 🔹 Guardar en AMBOS storages para máxima persistencia
    sessionStorage.setItem(storageKey, data);
    localStorage.setItem(storageKey, data);
  }, [selectedIds, storageKey]);

  useEffect(() => {
    if (!items.length) return;
    setSelectedIds((prev) => {
      const valid = new Set([
        ...items.map((it) => it.__key),
        ...(hasNotasSupervisora ? [NOTES_KEY] : []),
      ]);
      const next = new Set([...prev].filter((k) => valid.has(k)));
      return next;
    });
  }, [items, hasNotasSupervisora]);

  // 🛡️ Cleanup: restaurar estilos si el componente se desmonta durante drag
  useEffect(() => {
    return () => {
      if (dragEndTimeoutRef.current) clearTimeout(dragEndTimeoutRef.current);
      document.body.style.userSelect = '';
    };
  }, []);

  // 🛡️ Bloquear scroll nativo SOLO sobre el drag handle (touchAction:none en handle, resto scrollea libre)
  // El listener de document ya no es necesario porque el handle tiene touchAction:'none' directo.

  // ========= Bloqueos de navegación / back =========
  useEffect(() => {
    sessionStorage.setItem("enDetalleCompra", "true");

    const handleBeforeUnload = (e) => {
      if (isConfirmingRef.current) return;
      e.preventDefault();
      e.returnValue =
        "⚠️ ¿Estás seguro que quieres salir? No se guardarán los cambios ⚠️";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    const handlePopState = () => {
      if (isConfirmingRef.current) return navigate(-1);

      const confirmar = window.confirm(
        "⚠️ ¿Estás seguro que quieres salir? No se guardarán los cambios. ⚠️"
      );
      if (confirmar) {
        sessionStorage.removeItem("enDetalleCompra");
        navigate("/comprador");
      } else {
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  // ========= 🔹 MEJORA 3: Autoscroll nativo de @hello-pangea/dnd =========
  const onDragStart = () => {
    if (dragEndTimeoutRef.current) {
      clearTimeout(dragEndTimeoutRef.current);
      dragEndTimeoutRef.current = null;
    }
    isDraggingRef.current = true;
    setIsDragging(true);
    document.body.style.userSelect = 'none';
  };

  const onDragEnd = (result) => {
    document.body.style.userSelect = '';

    // Bloquea el click sintético que dispara touchend tras el drag en móvil.
    // Se registra en fase de captura para interceptarlo antes de que llegue
    // a cualquier onClick del árbol. Se auto-elimina tras el primer click o 600ms.
    const blockNextClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
      document.removeEventListener('click', blockNextClick, true);
    };
    document.addEventListener('click', blockNextClick, true);
    setTimeout(() => document.removeEventListener('click', blockNextClick, true), 600);

    dragEndTimeoutRef.current = setTimeout(() => {
      isDraggingRef.current = false;
      setIsDragging(false);
      dragEndTimeoutRef.current = null;
    }, 300);

    if (!result.destination) return;
    const reordered = Array.from(items);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setItems(reordered);
    const ordenIds = reordered.map((item) => item.id);
    localStorage.setItem(`compra_${id}_orden`, JSON.stringify(ordenIds));
  };

  const toggleSelect = (itemKey) => {
    // 🛡️ Bloquear toggles durante y justo después del drag
    if (isDraggingRef.current || isDragging) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(itemKey) ? next.delete(itemKey) : next.add(itemKey);
      return next;
    });
  };

  // 🔹 MEJORA 2: NO limpiar datos al volver atrás (solo se limpian al confirmar)
  const handleBack = async () => {
    openConfirm({
      title: "Salir sin guardar",
      message: "⚠️ Se borrarán todos los cambios realizados. ¿Quieres continuar?",
      onConfirm: async () => {
        try {
          const fd = new FormData();
          fd.append("id_compra", id);
          await axios.post(`/api/compras/borrar_cambios`, fd);

          closeConfirm();
          showToast("✅ Cambios descartados", "success");
          setTimeout(() => navigate("/comprador"), 800);
        } catch (error) {
          console.error(error);
          closeConfirm();
          showToast("❌ Error al borrar los cambios", "error");
        }
      },
    });
  };

  const cambiarProducto = (compra, productoSeleccionado) => {
    // Excluir los ya marcados "No sustituir": si no, reaparecen al volver de CrearCompras.
    const productosVigentes = (productos || []).filter((p) => !eliminadosSet.has(p.id));
    navigate(`/supervisora/crearCompras/${id_piso}`, {
      state: {
        id,
        id_piso,
        user_supervisora: usuario,
        abreviatura,
        productos: productosVigentes,
        productoSeleccionado,
        modoNoHay: true,
        origen: "DetalleCompra",
      },
    });
  };

  const marcarNoSustituible = async (productoSeleccionado) => {
    openConfirm({
      title: "No sustituir",
      message: "No sustituir y eliminar el producto de la lista. ¿Deseas continuar?",
      variant: 'danger',
      onConfirm: async () => {
        try {
          const fd = new FormData();
          fd.append("id", id);
          fd.append("productoSeleccionado", JSON.stringify({
            id: productoSeleccionado.id,
            nombre: productoSeleccionado.nombre,
            precio: productoSeleccionado.precio,
            cantidad: productoSeleccionado.cantidad,
          }));
          await axios.post(`/api/compras/cambios_compras_no_sust`, fd);
          setItems((prev) => prev.filter((p) => p.__key !== productoSeleccionado.__key));
          setSelectedIds((prev) => {
            const n = new Set(prev);
            n.delete(productoSeleccionado.__key);
            return n;
          });
          setEliminadosSet((prev) => {
            const n = new Set(prev);
            if (productoSeleccionado.id != null) n.add(productoSeleccionado.id);
            return n;
          });
          closeConfirm();
          showToast("✅ Marcado como 'No sustituir'", "success");
        } catch (e) {
          console.error(e);
          closeConfirm();
          showToast("❌ Error al marcar como no sustituible", "error");
        }
      },
    });
  };

  // 🔹 MEJORA 2: Función helper para limpiar TODOS los datos de una compra
  const limpiarDatosCompra = (idCompra) => {
    if (!idCompra) return;
    
    // sessionStorage
    sessionStorage.removeItem(`detalleCompra:${idCompra}:selectedIds`);
    sessionStorage.removeItem(`detalleCompra:${idCompra}:productos`);
    sessionStorage.removeItem(`detalleCompra:${idCompra}:state`);
    sessionStorage.removeItem("detalleCompra:lastId");
    
    // localStorage
    localStorage.removeItem(`detalleCompra:${idCompra}:selectedIds`);
    localStorage.removeItem(`compra_${idCompra}_orden`);
  };

  const handleConfirmSubmit = async () => {
    const fd = new FormData();
    fd.append("id", id);
    fd.append("notas_comprador", notas);
    if (pisoNecesitaEleccion) {
      if (supermercadoElegido !== 'B' && supermercadoElegido !== 'M') {
        alert('Selecciona supermercado: B o M');
        return;
      }
      fd.append('supermercado_elegido', supermercadoElegido);
    }

    try {
      await axios.post(`/api/compras/realizar_compra`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setModalOpen(false);
      showToast("✅ Compra realizada correctamente", "success");
      isConfirmingRef.current = true;

      // 🔹 MEJORA 2: Limpieza completa tras confirmar
      limpiarDatosCompra(id);
      try { if (supermercadoKey) sessionStorage.removeItem(supermercadoKey); } catch (e) { /* ignore */ }

      setTimeout(() => navigate("/comprador", { replace: true }), 1300);
    } catch (err) {
      console.error(err);
      showToast("❌ Error al confirmar la compra", "error");
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        maxWidth: 650,
        margin: "0 auto",
        padding: 20,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      {mostrarPromptSupermercado && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 4000, background: "rgba(15,23,42,0.75)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "28px 26px", maxWidth: 340, width: "100%", textAlign: "center", boxShadow: "0 20px 50px rgba(0,0,0,0.35)" }}>
            <div style={{ fontSize: 34, marginBottom: 10 }}>🛒</div>
            <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 8, color: "#1e293b" }}>¿En qué supermercado vas a comprar?</div>
            <div style={{ fontSize: 13.5, color: "#64748b", marginBottom: 20 }}>Este piso no tiene un supermercado fijo asignado. Elige uno para ver los productos en tu orden habitual.</div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              {["B", "M"].map((s) => (
                <button key={s} onClick={() => elegirSupermercadoInicial(s)} style={{
                  flex: 1, padding: "16px 0", borderRadius: 12, border: "2px solid #e5e7eb", background: "#f8fafc",
                  fontSize: 20, fontWeight: 800, color: "#1e293b", cursor: "pointer",
                }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <h1 style={{ textAlign: "center", marginBottom: 10 }}>
        Compra de {abreviatura}
      </h1>

      <p
        style={{
          fontWeight: "bold",
          marginBottom: 25,
          fontSize: 30,
          textAlign: "center",
        }}
      >
        Supervisora:{" "}
        <span style={{ color: "#2c7a7b" }}>
          {routeState.user_supervisora}
        </span>
      </p>

      <p
        style={{
          fontWeight: "bold",
          marginBottom: 25,
          fontSize: 30,
          textAlign: "center",
        }}
      >
        Precio total:{" "}
        <span style={{ color: "#2c7a7b" }}>
          {Number(precio_total).toFixed(2)} €
        </span>
      </p>

      {hasNotasSupervisora && (() => {
        const isSelected = selectedIds.has(NOTES_KEY);
        return (
          <div
            onClick={() => {
              // 🛡️ Prevenir clicks durante drag
              if (isDragging || isDraggingRef.current) return;
              toggleSelect(NOTES_KEY);
            }}
            style={{
              cursor: "pointer",
              background: isSelected ? "#1e1e1e" : "#fff5f5",
              color: isSelected ? "#eee" : "#7f1d1d",
              border: "3px solid #e53935",
              borderLeftWidth: "8px",
              borderRadius: 12,
              padding: "14px 18px",
              margin: "0 auto 18px auto",
              maxWidth: 600,
              boxShadow: isSelected
                ? "0 2px 10px rgba(0,0,0,0.3)"
                : "0 0 0 3px rgba(229,57,53,0.15), 0 6px 14px rgba(229,57,53,0.15)",
              transition: "all 0.2s ease-in-out",
              textAlign: "left",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>⚠️</span>
              <strong style={{ fontSize: 18 }}>
                Notas de la supervisora (toca para marcar)
              </strong>
            </div>
            <div style={{ marginTop: 8, fontSize: 17, lineHeight: 1.35 }}>
              {routeState.notas_supervisora}
            </div>
          </div>
        );
      })()}

      <DragDropContext
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        autoScrollerOptions={{
          startFromPercentage: 0.12,
          maxScrollAtPercentage: 0.2,
          maxPixelScroll: 20,
          ease: (t) => t,
          durationDampening: {
            stopDampeningAt: 2000,
            accelerateAt: 800,
          },
        }}
      >
        <Droppable droppableId="productosDroppable">
          {(provided) => (
            <div 
              {...provided.droppableProps} 
              ref={(el) => {
                provided.innerRef(el);
                droppableRef.current = el;
              }}
            >
              {items.map((prod, index) => {
                const key = prod.__key; // clave estable por ítem
                const isSelected = selectedIds.has(key);
                return (
                  <Draggable
                    key={String(key)}
                    draggableId={String(key)}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        onClick={() => {
                          if (isDragging || isDraggingRef.current) return;
                          toggleSelect(key);
                        }}
                        style={{
                          userSelect: "none",
                          width: "100%",
                          padding: 10,
                          marginBottom: 12,
                          borderRadius: 12,
                          border: snapshot.isDragging ? "2px solid #1976d2" : "1px solid #ddd",
                          backgroundColor: isSelected ? "#1e1e1e" : "#fff",
                          color: isSelected ? "#eee" : "#222",
                          display: "flex",
                          flexDirection: "row",
                          boxShadow: snapshot.isDragging
                            ? "0 12px 32px rgba(25,118,210,0.35)"
                            : isSelected
                            ? "0 2px 10px rgba(0,0,0,0.3)"
                            : "2px 2px 6px rgba(0,0,0,0.1)",
                          minHeight: "100px",
                          willChange: snapshot.isDragging ? "transform" : "auto",
                          ...provided.draggableProps.style,
                        }}
                      >
                        <AuthImage
                          src={
                            typeof prod.imagen === "string"
                              ? prod.imagen.startsWith("http")
                                ? prod.imagen
                                : `/api/productos_sustitutivos/${prod.imagen}`
                              : prod.imagen
                          }
                          alt={prod.nombre}
                          style={{
                            height: "55%",
                            width: "35%",
                            objectFit: "contain",
                            borderRadius: 8,
                            flexShrink: 0,
                            marginRight: 12,
                          }}
                        />

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            flex: 1,
                          }}
                        >
                          <div style={{ fontSize: 16, fontWeight: "600" }}>
                            {prod.nombre}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: isSelected
                                  ? "#f2f2f2"
                                  : "#00796b",
                                color: isSelected ? "#121212" : "white",
                                fontWeight: "800",
                                fontSize: 24,
                                width: 35,
                                height: 35,
                                borderRadius: 8,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                userSelect: "none",
                                transition:
                                  "background-color 0.3s, color 0.3s",
                              }}
                            >
                              {prod.cantidad}
                            </div>
                            <div style={{ fontSize: 18, fontWeight: "700" }}>
                              {prod.precio.toFixed(2)} €
                            </div>
                          </div>

                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 10,
                              marginTop: "8px",
                              width: "100%",
                            }}
                          >
                            {!isSelected && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openConfirm({
                                    title: "Sustituir producto",
                                    message: "Vas a sustituir por otro producto. ¿Deseas continuar?",
                                    variant: 'info',
                                    onConfirm: () => { closeConfirm(); cambiarProducto(items, prod); },
                                  });
                                }}
                                style={{
                                  marginTop: 0,
                                  padding: "10px 16px",
                                  background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "12px",
                                  fontSize: "16px",
                                  fontWeight: 800,
                                  cursor: "pointer",
                                  boxShadow: "0 6px 14px rgba(37,99,235,0.35)",
                                  transition: "transform .1s ease, box-shadow .1s ease",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = "scale(1.03)";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                }}
                              >
                                Sustituir
                              </button>
                            )}
                            {!isSelected && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  marcarNoSustituible(prod);
                                }}
                                style={{
                                  marginTop: 0,
                                  padding: "10px 16px",
                                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "12px",
                                  fontSize: "16px",
                                  fontWeight: 800,
                                  cursor: "pointer",
                                  boxShadow: "0 6px 14px rgba(220,38,38,0.35)",
                                  transition: "transform .1s ease, box-shadow .1s ease",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = "scale(1.03)";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                }}
                                title="Marcar este producto como no sustituible"
                              >
                                No sust.
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Handle exclusivo de arrastre - lado derecho */}
                        <div
                          {...provided.dragHandleProps}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            flexShrink: 0,
                            alignSelf: 'stretch',
                            cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                            color: isSelected ? '#888' : '#ccc',
                            fontSize: 20,
                            marginLeft: 6,
                            touchAction: 'none',
                          }}
                          title="Arrastra para reordenar"
                        >
                          ⠿
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Botones */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 30,
        }}
      >
        <button
          onClick={handleBack}
          style={{
            padding: "14px 28px",
            backgroundColor: "#555",
            color: "white",
            border: "none",
            borderRadius: 8,
            fontSize: "18px",
            fontWeight: "bold",
            cursor: "pointer",
            transition: "background-color 0.3s ease",
          }}
        >
          Atrás
        </button>

        <button
          onClick={() => {
            const requiredCount = items.length + (hasNotasSupervisora ? 1 : 0);
            if (selectedIds.size !== requiredCount) {
              showToast(
                hasNotasSupervisora
                  ? "⚠️ Selecciona todos los productos y marca las notas de la supervisora."
                  : "⚠️ Selecciona todos los productos para confirmar.",
                "error"
              );
              return;
            }
            // Asegura 2 decimales al abrir el modal
            setPrecioInput((v) => Number(v || 0).toFixed(2));
            setModalOpen(true);
          }}
          style={{
            padding: "14px 28px",
            backgroundColor:
              selectedIds.size === (items.length + (hasNotasSupervisora ? 1 : 0)) ? "#00796b" : "#aaa",
            color: "white",
            border: "none",
            borderRadius: 8,
            fontSize: "18px",
            fontWeight: "bold",
            cursor:
              selectedIds.size === (items.length + (hasNotasSupervisora ? 1 : 0)) ? "pointer" : "not-allowed",
            transition: "background-color 0.3s ease",
          }}
        >
          Confirmar compra
        </button>
      </div>

      {/* Modal confirmación compra */}
      {modalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 14,
              padding: 20,
              width: "min(500px, 92vw)",
              boxShadow: "0 16px 36px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 0, marginBottom: 10, color: '#111827' }}>Confirmar Compra</div>
              <label style={{ display: "block", marginBottom: 8 }}>
                Notas (opcional):
                <textarea
                  value={notas}
                  onChange={handleNotasChange}
                  rows={3}
                  placeholder="(opcional) Escribe aquí notas para la compra"
                  style={{
                    width: "100%",
                    padding: 10,
                    marginTop: 6,
                    marginBottom: 12,
                    fontSize: 16,
                    resize: "vertical",
                    border: '1px solid #e5e7eb',
                    borderRadius: 10,
                  }}
                />
              </label>

              {/* El piso no tiene supermercado fijo: ya se eligió B/M al entrar
                  en la compra (ver modal al abrir la página), aquí solo se
                  recuerda cuál para no preguntarlo dos veces. */}
              {pisoNecesitaEleccion && (
                <div style={{ marginBottom: 12, fontSize: 13.5, color: '#374151' }}>
                  Supermercado: <strong>{supermercadoElegido || '—'}</strong>
                </div>
              )}

            <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => setModalOpen(false)}
                style={{
                  padding: "10px 14px",
                  background: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontWeight: 700,
                  color: "#374151",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmSubmit}
                style={{
                  padding: "10px 16px",
                  background: "linear-gradient(135deg, #22c55e, #16a34a)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontWeight: 800,
                  boxShadow: "0 6px 14px rgba(22,163,74,0.35)",
                }}
              >
                Confirmar ✓
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal reutilizable (estilo Comprador.js) */}
      {confirmData.open && (
        <div
          onClick={closeConfirm}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: '18px 20px',
              width: 'min(500px, 92vw)',
              maxWidth: '92vw',
              boxShadow: '0 16px 36px rgba(0,0,0,0.3)'
            }}
            role="dialog"
            aria-modal="true"
          >
            {(() => {
              const variant = confirmData.variant;
              const badgeBg = variant === 'danger'
                ? 'linear-gradient(135deg,#fee2e2,#fecaca)'
                : variant === 'warning'
                ? 'linear-gradient(135deg,#fef3c7,#fde68a)'
                : 'linear-gradient(135deg,#eef2ff,#dbeafe)';
              const badgeColor = variant === 'danger' ? '#7f1d1d' : variant === 'warning' ? '#78350f' : '#1e3a8a';
              const msgBg = variant === 'danger' ? '#fef2f2' : variant === 'warning' ? '#fffbeb' : '#eff6ff';
              const msgColor = variant === 'danger' ? '#7f1d1d' : variant === 'warning' ? '#78350f' : '#1e3a8a';
              return (
                <>
                  <div style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 10, fontWeight: 900, color: badgeColor, background: badgeBg, marginBottom: 10 }}>
                    {confirmData.title || 'Confirmar'}
                  </div>
                  <div style={{ fontSize: 16, color: msgColor, lineHeight: 1.5, wordBreak: 'break-word', background: msgBg, padding: '10px 12px', borderRadius: 10 }}>
                    {confirmData.message}
                  </div>
                </>
              );
            })()}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button onClick={closeConfirm} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontWeight: 700 }}>Cancelar</button>
              <button
                onClick={() => { const fn = confirmData.onConfirm; if (typeof fn === 'function') fn(); }}
                style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontWeight: 800, boxShadow: '0 6px 14px rgba(22,163,74,0.35)' }}
              >
                Confirmar ✓
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      {toast.visible && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div
            style={{
              background: toast.type === 'success' ? '#e8f5e9' : toast.type === 'error' ? '#fdecea' : '#eef2ff',
              color: toast.type === 'success' ? '#1b5e20' : toast.type === 'error' ? '#7f1d1d' : '#1e3a8a',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 16px 36px rgba(0,0,0,0.25)',
              padding: '16px 22px',
              borderRadius: 14,

              fontSize: 18,
              fontWeight: 700,
              maxWidth: 520,
              textAlign: 'center',
            }}
            role="status"
            aria-live="polite"
          >
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default DetalleCompra;
